<template>
  <div>
    <Title title="Administrar nodos"
           subtitle="Revisa los detalles y configura opciones por centro de distribución"/>
    <el-row type="flex" justify="space-between" align="middle" class="toolbar">
      <el-col :span="14">
        <el-input v-model="search"
                  placeholder="Buscar por nodo, ID, sistema, punto o unidad de negocio"
                  prefix-icon="el-icon-search"
                  clearable
                  @input="scheduleSearch"/>
      </el-col>
      <el-col :span="9" class="save-actions">
        <span v-if="pendingCount" class="pending-count">{{pendingCount}} nodos pendientes</span>
        <el-button type="success"
                   :loading="saving"
                   :disabled="!hasPendingChanges || hasInvalidJson"
                   @click="saveConfigs">
          {{auditRetryPayload ? 'Reintentar auditoría' : 'Guardar cambios'}}
        </el-button>
      </el-col>
    </el-row>

    <el-tabs type="card">
      <el-tab-pane label="Guías de despacho">
        <el-table :data="distributionCenters"
                  :height="tableHeight"
                  v-loading="loading || saving"
                  row-key="id"
                  border
                  size="mini">
          <el-table-column fixed="left" prop="name" label="Nodo" min-width="190"/>
          <el-table-column prop="id" label="ID" width="85"/>
          <el-table-column label="Unidad de negocio" min-width="170">
            <template slot-scope="scope">
              {{scope.row.business_unit ? scope.row.business_unit.name : '-'}}
            </template>
          </el-table-column>
          <el-table-column prop="systems" label="Sistemas" min-width="140"/>
          <el-table-column label="Inicio jornada" min-width="145">
            <template slot-scope="scope">
              {{formatTime(scope.row.start_working_day)}}
            </template>
          </el-table-column>
          <el-table-column label="Fin jornada" min-width="145">
            <template slot-scope="scope">
              {{formatTime(scope.row.finish_working_day)}}
            </template>
          </el-table-column>
          <el-table-column prop="points_id" label="Punto" width="100"/>
          <el-table-column v-for="key in nonBooleanConfigKeys"
                           :key="key"
                           :label="configLabel(key)"
                           min-width="190">
            <template slot-scope="scope">
              <el-input v-if="configTypes[key] === 'string'"
                        :value="configValue(scope.row, key)"
                        :disabled="saving || Boolean(auditRetryPayload)"
                        size="mini"
                        @input="changeConfig(scope.row, key, $event)"/>
              <el-input-number v-else-if="configTypes[key] === 'number'"
                               :value="configValue(scope.row, key)"
                               :disabled="saving || Boolean(auditRetryPayload)"
                               size="mini"
                               @change="changeConfig(scope.row, key, $event)"/>
              <el-input v-else
                        :value="jsonDraft(scope.row, key)"
                        :disabled="saving || Boolean(auditRetryPayload)"
                        :class="{ 'invalid-json': jsonErrors[jsonDraftKey(scope.row, key)] }"
                        type="textarea"
                        :rows="2"
                        size="mini"
                        @input="changeJsonConfig(scope.row, key, $event)"/>
            </template>
          </el-table-column>
          <el-table-column v-for="key in booleanConfigKeys"
                           :key="key"
                           fixed="right"
                           width="185"
                           align="center">
            <template slot="header">
              <el-tooltip content="Marcar o desmarcar todos los nodos de esta página"
                          placement="top">
                <el-checkbox :value="isColumnFullyChecked(key)"
                             :indeterminate="isColumnPartiallyChecked(key)"
                             :disabled="saving || Boolean(auditRetryPayload)"
                             @change="setCurrentPageColumn(key, $event)">
                  {{configLabel(key)}}
                </el-checkbox>
              </el-tooltip>
            </template>
            <template slot-scope="scope">
              <el-checkbox :value="Boolean(configValue(scope.row, key))"
                           :disabled="saving || Boolean(auditRetryPayload)"
                           @click.native.stop="handleBooleanClick($event, scope.row, key)"
                           @change="handleBooleanChange(scope.row, key, $event)">
                {{configLabel(key)}}
              </el-checkbox>
            </template>
          </el-table-column>
          <el-table-column v-if="!configKeys.length"
                           fixed="right"
                           label="Configuración"
                           width="190">
            <template>
              Sin opciones configurables
            </template>
          </el-table-column>
        </el-table>
        <el-pagination class="pagination"
                       :current-page="currentPage"
                       :page-size="pageSize"
                       :page-sizes="[10, 25, 50, 100]"
                       :total="totalRows"
                       layout="total, sizes, prev, pager, next, jumper"
                       @current-change="changePage"
                       @size-change="changePageSize"/>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import Title from '../components/base/Title.vue';
import { apiRequest } from '../../backend/vue-axios/axios';

const clone = value => JSON.parse(JSON.stringify(value));

export default {
  name: 'DistributionCenterConfigs',
  components: {
    Title,
  },
  data() {
    return {
      distributionCenters: [],
      configTypes: {},
      originalConfigs: {},
      pendingConfigs: {},
      jsonDrafts: {},
      jsonErrors: {},
      search: '',
      currentPage: 1,
      pageSize: 25,
      totalRows: 0,
      tableHeight: 'calc(100vh - 310px)',
      loading: false,
      saving: false,
      requestId: 0,
      searchTimer: null,
      auditRetryPayload: null,
      auditRetryUpdates: [],
      booleanRangeAnchors: {},
      pendingBooleanRange: null,
    };
  },
  computed: {
    configKeys() {
      return Object.keys(this.configTypes).sort();
    },
    booleanConfigKeys() {
      return this.configKeys.filter(key => this.configTypes[key] === 'boolean');
    },
    nonBooleanConfigKeys() {
      return this.configKeys.filter(key => this.configTypes[key] !== 'boolean');
    },
    pendingCount() {
      return Object.keys(this.pendingConfigs).length;
    },
    hasPendingChanges() {
      return this.pendingCount > 0
        || Object.keys(this.jsonErrors).length > 0
        || Boolean(this.auditRetryPayload);
    },
    hasInvalidJson() {
      return Object.keys(this.jsonErrors).length > 0;
    },
  },
  created() {
    this.loadPage();
  },
  mounted() {
    window.addEventListener('beforeunload', this.confirmBrowserLeave);
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    window.removeEventListener('beforeunload', this.confirmBrowserLeave);
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasPendingChanges) {
      next();
      return;
    }
    this.$confirm('Hay cambios pendientes de guardar o auditar. ¿Quieres salir de todas formas?', 'Cambios pendientes', {
      confirmButtonText: 'Salir',
      cancelButtonText: 'Continuar editando',
      type: 'warning',
    }).then(() => next()).catch(() => next(false));
  },
  methods: {
    loadPage() {
      const activeRequest = this.requestId + 1;
      this.requestId = activeRequest;
      this.loading = true;
      apiRequest({
        module: 'crud',
        service: 'get_distribution_center_configs',
        params: {
          search: this.search.trim(),
          limit: this.pageSize,
          offset: (this.currentPage - 1) * this.pageSize,
        },
      }).then((response) => {
        if (activeRequest !== this.requestId) return;
        this.totalRows = response.data.total;
        this.configTypes = response.data.config_types || {};
        this.distributionCenters = response.data.items.map((center) => {
          const baseline = this.originalConfigs[center.id]
            || (this.pendingConfigs[center.id] && this.pendingConfigs[center.id].previous_configs)
            || center.configs || {};
          const configs = this.pendingConfigs[center.id]
            ? this.pendingConfigs[center.id].configs
            : center.configs || {};
          this.$set(this.originalConfigs, center.id, clone(baseline));
          this.initializeJsonDrafts(center.id, configs);
          return Object.assign({}, center, { configs: clone(configs) });
        });
      }).catch(() => {
        if (activeRequest === this.requestId) {
          this.$message.error('Error al obtener los nodos');
        }
      }).finally(() => {
        if (activeRequest === this.requestId) this.loading = false;
      });
    },
    scheduleSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1;
        this.loadPage();
      }, 300);
    },
    changePage(page) {
      this.currentPage = page;
      this.loadPage();
    },
    changePageSize(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.loadPage();
    },
    defaultConfigValue(key) {
      const type = this.configTypes[key];
      if (type === 'boolean') return false;
      if (type === 'number') return 0;
      if (type === 'string') return '';
      if (type === 'array') return [];
      return {};
    },
    configValue(row, key) {
      if (Object.prototype.hasOwnProperty.call(row.configs, key)) return row.configs[key];
      return this.defaultConfigValue(key);
    },
    initializeJsonDrafts(id, configs) {
      this.configKeys.forEach((key) => {
        if (this.configTypes[key] !== 'string'
            && this.configTypes[key] !== 'number'
            && this.configTypes[key] !== 'boolean') {
          const draftKey = `${id}:${key}`;
          const value = Object.prototype.hasOwnProperty.call(configs, key)
            ? configs[key] : this.defaultConfigValue(key);
          this.$set(this.jsonDrafts, draftKey, JSON.stringify(value, null, 2));
          this.$delete(this.jsonErrors, draftKey);
        }
      });
    },
    jsonDraftKey(row, key) {
      return `${row.id}:${key}`;
    },
    jsonDraft(row, key) {
      return this.jsonDrafts[this.jsonDraftKey(row, key)];
    },
    changeJsonConfig(row, key, value) {
      const draftKey = this.jsonDraftKey(row, key);
      this.$set(this.jsonDrafts, draftKey, value);
      try {
        this.changeConfig(row, key, JSON.parse(value));
        this.$delete(this.jsonErrors, draftKey);
      } catch (error) {
        this.$set(this.jsonErrors, draftKey, 'JSON inválido');
      }
    },
    changeConfig(row, key, value) {
      const original = this.originalConfigs[row.id] || {};
      const defaultValue = this.defaultConfigValue(key);
      if (!Object.prototype.hasOwnProperty.call(original, key)
          && JSON.stringify(value) === JSON.stringify(defaultValue)) {
        this.$delete(row.configs, key);
      } else {
        this.$set(row.configs, key, value);
      }

      if (this.configsEqual(row.configs, original)) {
        this.$delete(this.pendingConfigs, row.id);
      } else {
        this.$set(this.pendingConfigs, row.id, {
          id: row.id,
          name: row.name,
          previous_configs: clone(original),
          configs: clone(row.configs),
        });
      }
    },
    configsEqual(left, right) {
      const keys = new Set([...Object.keys(left), ...Object.keys(right), ...this.configKeys]);
      return Array.from(keys).every((key) => {
        const leftValue = Object.prototype.hasOwnProperty.call(left, key)
          ? left[key] : this.defaultConfigValue(key);
        const rightValue = Object.prototype.hasOwnProperty.call(right, key)
          ? right[key] : this.defaultConfigValue(key);
        return JSON.stringify(leftValue) === JSON.stringify(rightValue);
      });
    },
    configLabel(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, character => character.toUpperCase());
    },
    formatTime(value) {
      if (!value) return '-';
      return value.length > 10 ? value.substring(11, 16) : value;
    },
    isColumnFullyChecked(key) {
      return this.distributionCenters.length > 0
        && this.distributionCenters.every(row => Boolean(this.configValue(row, key)));
    },
    isColumnPartiallyChecked(key) {
      const checkedCount = this.distributionCenters
        .filter(row => Boolean(this.configValue(row, key))).length;
      return checkedCount > 0 && checkedCount < this.distributionCenters.length;
    },
    setCurrentPageColumn(key, checked) {
      this.distributionCenters.forEach((row) => {
        this.changeConfig(row, key, checked);
      });
      if (this.distributionCenters.length) {
        this.$set(this.booleanRangeAnchors, key, this.distributionCenters[
          this.distributionCenters.length - 1
        ].id);
      }
    },
    handleBooleanClick(event, row, key) {
      const rowIndex = this.distributionCenters.findIndex(center => center.id === row.id);
      this.pendingBooleanRange = null;
      if (event.shiftKey && rowIndex >= 0) {
        const anchorId = this.booleanRangeAnchors[key];
        const anchorIndex = this.distributionCenters.findIndex(center => center.id === anchorId);
        if (anchorIndex !== undefined) {
          if (anchorIndex >= 0) {
            this.pendingBooleanRange = {
              key,
              start: Math.min(anchorIndex, rowIndex),
              end: Math.max(anchorIndex, rowIndex),
            };
          }
        }
      }
      if (!event.shiftKey && rowIndex >= 0) {
        this.$set(this.booleanRangeAnchors, key, row.id);
      }
    },
    handleBooleanChange(row, key, checked) {
      const range = this.pendingBooleanRange;
      if (range && range.key === key) {
        for (let index = range.start; index <= range.end; index += 1) {
          this.changeConfig(this.distributionCenters[index], key, checked);
        }
        this.pendingBooleanRange = null;
        return;
      }
      this.changeConfig(row, key, checked);
    },
    buildAuditPayload(updates) {
      return {
        case_use: 'distribution_center_configuration_update',
        quantity: updates.length,
        comments: `Actualización de configuración de ${updates.length} nodo(s)`,
        action_data: {
          action: 'distribution_center_configuration_update',
          nodes: updates.map(update => ({
            distribution_center_id: update.id,
            distribution_center_name: update.name,
            changed_fields: this.configKeys.filter(key => JSON.stringify(update.configs[key])
              !== JSON.stringify(update.previous_configs[key])),
            previous_configs: update.previous_configs,
            configs: update.configs,
          })),
        },
      };
    },
    saveConfigs() {
      if (this.auditRetryPayload) {
        this.saveAudit(this.auditRetryPayload, this.auditRetryUpdates);
        return;
      }
      if (!this.pendingCount || Object.keys(this.jsonErrors).length) return;

      const updates = Object.values(this.pendingConfigs).map(update => clone(update));
      const auditPayload = this.buildAuditPayload(updates);
      this.saving = true;
      apiRequest({
        module: 'crud',
        service: 'update_distribution_center_configs_bulk',
        params: {
          updates: updates.map(update => ({ id: update.id, configs: update.configs })),
        },
      }).then(() => {
        this.auditRetryPayload = auditPayload;
        this.auditRetryUpdates = updates;
        this.sendAudit();
      }).catch(() => {
        this.saving = false;
        this.$message.error('No se pudieron guardar los cambios; no se registró auditoría');
      });
    },
    sendAudit() {
      apiRequest({
        module: 'toolkit',
        service: 'audit_node_configuration',
        params: this.auditRetryPayload,
      }).then(() => {
        this.auditRetryUpdates.forEach((update) => {
          this.$set(this.originalConfigs, update.id, clone(update.configs));
        });
        this.pendingConfigs = {};
        this.auditRetryPayload = null;
        this.auditRetryUpdates = [];
        this.$message.success('Cambios guardados y registrados en auditoría');
      }).catch(() => {
        this.$message.warning('La configuración se guardó, pero la auditoría no respondió. Reintenta para completar el registro.');
      }).finally(() => {
        this.saving = false;
      });
    },
    saveAudit(payload, updates) {
      this.auditRetryPayload = payload;
      this.auditRetryUpdates = updates;
      this.saving = true;
      this.sendAudit();
    },
    confirmBrowserLeave(event) {
      if (!this.hasPendingChanges) return undefined;
      event.preventDefault();
      Object.defineProperty(event, 'returnValue', { value: '' });
      return '';
    },
  },
};
</script>

<style lang="scss" scoped>
.toolbar {
  margin-bottom: 16px;
}

.save-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}

.pending-count {
  color: #606266;
  white-space: nowrap;
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

.invalid-json ::v-deep textarea {
  border-color: #f56c6c;
}
</style>
